import { Card, CardContent, Typography, Box, Tooltip } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { tokens } from '../../theme'

// Animation for number counter
const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/**
 * DASH2 StatCard - Bold, dark theme with glassmorphism
 */
const StatCard = ({
  title,
  value,
  uncertainty,
  subtitle,
  icon,
  color = '#A0CE4E',
  trend,
  onClick
}) => {
  const theme = useTheme()
  const palette = theme.palette

  // Get gradient colors based on color prop
  const getGradient = () => {
    if (color.includes('A0CE') || color.includes('7CB3')) {
      return `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`
    }
    if (color.includes('9c27') || color.includes('8E24') || color.includes('9F7A')) {
      return `linear-gradient(135deg, ${palette.accent.purple} 0%, #7C3AED 100%)`
    }
    if (color.includes('4ECDC') || color.includes('00897')) {
      return `linear-gradient(135deg, ${palette.accent.teal} 0%, #38B2AC 100%)`
    }
    if (color.includes('FF6B') || color.includes('F57C') || color.includes('ff98')) {
      return `linear-gradient(135deg, ${palette.accent.orange} 0%, #E53E3E 100%)`
    }
    if (color.includes('2196') || color.includes('63B3')) {
      return `linear-gradient(135deg, ${palette.accent.blue} 0%, #4299E1 100%)`
    }
    if (color.includes('4caf') || color.includes('4CAF')) {
      return `linear-gradient(135deg, ${palette.success.main} 0%, ${palette.success.dark} 100%)`
    }
    return `linear-gradient(135deg, ${color} 0%, ${color} 100%)`
  }

  const getGlowColor = () => {
    if (color.includes('A0CE') || color.includes('7CB3')) return palette.primary.main
    if (color.includes('9c27') || color.includes('8E24') || color.includes('9F7A')) return palette.accent.purple
    if (color.includes('4ECDC') || color.includes('00897')) return palette.accent.teal
    if (color.includes('FF6B') || color.includes('F57C') || color.includes('ff98')) return palette.accent.orange
    if (color.includes('2196') || color.includes('63B3')) return palette.accent.blue
    return color
  }

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(getGlowColor(), 0.3),
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(getGlowColor(), 0.1)}`,
          '& .stat-icon': {
            transform: 'scale(1.1)',
            boxShadow: `0 0 30px ${alpha(getGlowColor(), 0.4)}`
          }
        } : {
          borderColor: alpha(palette.text.primary, 0.1)
        },
        // Top accent line
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: getGradient(),
          opacity: 0.8
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: '16px !important', sm: '20px !important' } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                lineHeight: 1.5,
                display: 'block',
                mb: 0.75
              }}
            >
              {title}
            </Typography>


            {/* Value with optional uncertainty */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  color: 'text.primary',
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                  animation: `${countUp} 0.5s ease-out`
                }}
              >
                {value}
              </Typography>
              {uncertainty && (
                <Tooltip
                  title="Standard deviation (1σ) — represents typical variation from mean"
                  arrow
                  placement="top"
                >
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      cursor: 'help',
                      borderBottom: '1px dotted',
                      borderColor: 'text.disabled'
                    }}
                  >
                    {uncertainty}
                  </Typography>
                </Tooltip>
              )}
              {trend && (
                <Typography
                  component="span"
                  sx={{
                    color: trend > 0 ? palette.success.main : palette.error.main,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    ml: 0.5
                  }}
                >
                  {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                </Typography>
              )}
            </Box>

            {/* Subtitle */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1,
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Icon Badge */}
          <Box
            className="stat-icon"
            sx={{
              background: getGradient(),
              borderRadius: tokens.radius.sm,
              p: { xs: 0.75, sm: 1, md: 1.25 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 16px ${alpha(getGlowColor(), 0.3)}`,
              transition: `all ${tokens.transitions.normal}`,
              ml: { xs: 1, sm: 1.5, md: 2 },
              flexShrink: 0,
              '& .MuiSvgIcon-root': {
                color: palette.primary.contrastText,
                fontSize: { xs: 18, sm: 20, md: 24 }
              }
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default StatCard
